import { bunja } from 'bunja';

import { weakR230522NormalSubSocketBunja } from '.';
import type { SetSubscriptionCommandJsonValue } from '../../model/prex.v2.r230522.messages.subscription.client';
import { TablesJsonValue } from '../../model/prex.v2.r230522.messages.subscription.server';
import { createBatcher } from './batcher';

export interface R230522NormalSubSubscription {
  rows: string[]; // "*" for all rows, or specific row IDs
}

export type UnsubscribeFn = () => void;

export interface Handle {
  set(update: Partial<R230522NormalSubSubscription>): void;
  dispose(): void;
}

// 어떤 토픽들을 구독하는 중인지 관리함
export const weakR230522NormalSubStateBunja = bunja(() => {
  const { r230522NormalSubSocket } = bunja.use(weakR230522NormalSubSocketBunja);

  type R230522NormalSubTableTopic = keyof TablesJsonValue;
  type SubscriptionsTable = Record<R230522NormalSubTableTopic, Set<R230522NormalSubSubscription>>;
  const subscriptionsTable = {} as SubscriptionsTable;
  function getSetSubscriptionCommand(
    tableTopics = Object.keys(subscriptionsTable) as R230522NormalSubTableTopic[],
  ): SetSubscriptionCommandJsonValue {
    const setSubscriptionCommand: SetSubscriptionCommandJsonValue = {};
    for (const tableTopic of tableTopics) {
      const rows = getRows(tableTopic);
      const subscription = { rows };
      setSubscriptionCommand[tableTopic as keyof SetSubscriptionCommandJsonValue] = subscription;
    }
    return setSubscriptionCommand;
  }
  function getSubscriptions(tableTopic: R230522NormalSubTableTopic): Set<R230522NormalSubSubscription> {
    return (subscriptionsTable[tableTopic] ??= new Set());
  }
  function getRows(tableTopic: R230522NormalSubTableTopic): string[] {
    const subscriptions = getSubscriptions(tableTopic);
    const mergedRows = new Set<string>();
    for (const { rows } of subscriptions) for (const row of rows) mergedRows.add(row);
    if (mergedRows.has('*')) return ['*'];
    return Array.from(mergedRows).sort();
  }
  const batcher = createBatcher<R230522NormalSubTableTopic>(
    (dirtyTopics) => r230522NormalSubSocket.send({ s: getSetSubscriptionCommand(dirtyTopics) }),
    (tableTopic) => getRows(tableTopic).join(','),
  );

  function subscribe(
    tableTopic: R230522NormalSubTableTopic,
    subscription: R230522NormalSubSubscription,
  ): UnsubscribeFn {
    const subscriptions = getSubscriptions(tableTopic);
    batcher.touch(tableTopic);
    subscriptions.add(subscription);
    return () => {
      batcher.touch(tableTopic);
      subscriptions.delete(subscription);
    };
  }

  function createHandle(
    tableTopic: R230522NormalSubTableTopic,
    initialSubscription: Partial<R230522NormalSubSubscription> = {},
  ): Handle {
    const subscriptions = getSubscriptions(tableTopic);
    const subscription: R230522NormalSubSubscription = { rows: [] };
    batcher.touch(tableTopic);
    subscriptions.add(subscription);
    Object.assign(subscription, initialSubscription);
    return {
      set(update) {
        batcher.touch(tableTopic);
        Object.assign(subscription, update);
      },
      dispose() {
        batcher.touch(tableTopic);
        subscriptions.delete(subscription);
      },
    };
  }

  function createMultiTopicHandle(
    tableTopics: R230522NormalSubTableTopic[],
    initialSubscription: Partial<R230522NormalSubSubscription> = {},
  ): Handle {
    const handles = tableTopics.map((tableTopic) => createHandle(tableTopic, initialSubscription));
    return {
      set(update) {
        for (const handle of handles) handle.set(update);
      },
      dispose() {
        for (const handle of handles) handle.dispose();
      },
    };
  }

  bunja.effect(() =>
    r230522NormalSubSocket.on('connected', () => {
      r230522NormalSubSocket.send({ s: getSetSubscriptionCommand() });
    }),
  );

  return { subscribe, createHandle, createMultiTopicHandle };
});
weakR230522NormalSubStateBunja.debugLabel = 'weakR230522NormalSubStateBunja';
